import { useCallback, useRef, useState } from 'react';

import moment from 'moment';

const DATE_PRESETS = [
  { key: '30D', label: '30D', days: 30, defaultGroupBy: 'day' },
  { key: '90D', label: '90D', days: 90, defaultGroupBy: 'week' },
  { key: '6M', label: '6M', days: 180, defaultGroupBy: 'week' },
  { key: '1Y', label: '1Y', days: 365, defaultGroupBy: 'month' },
  { key: 'ALL', label: 'All', days: null, defaultGroupBy: 'month' },
];

const DEFAULT_PRESET = '90D';

const getDateRangeForPreset = presetKey => {
  const preset = DATE_PRESETS.find(p => p.key === presetKey);
  if (!preset || preset.days === null) return null;
  const end = moment().format('YYYY-MM-DD');
  const start = moment().subtract(preset.days, 'days').format('YYYY-MM-DD');
  return [start, end];
};

const getDefaultGroupBy = presetKey => {
  const preset = DATE_PRESETS.find(p => p.key === presetKey);
  return preset?.defaultGroupBy || 'week';
};

const buildTrendsParams = (tableParams, presetKey) => {
  const { created_at, ...rest } = tableParams;
  const range = getDateRangeForPreset(presetKey);
  return {
    ...rest,
    ...(range ? { created_at: range } : {}),
  };
};

export const useReportingTrends = fetchTrendsFn => {
  const [trends, setTrends] = useState([]);
  const [groupBy, setGroupBy] = useState(getDefaultGroupBy(DEFAULT_PRESET));
  const [datePreset, setDatePreset] = useState(DEFAULT_PRESET);

  const groupByRef = useRef(groupBy);
  const datePresetRef = useRef(datePreset);
  groupByRef.current = groupBy;
  datePresetRef.current = datePreset;

  const fetchTrends = useCallback(
    async (tableParams = {}, overrideGroupBy) => {
      const effectiveGroupBy = overrideGroupBy ?? groupByRef.current;
      const params = buildTrendsParams(tableParams, datePresetRef.current);
      const result = await fetchTrendsFn({ ...params, group_by: effectiveGroupBy });
      setTrends(result.trends);
    },
    [fetchTrendsFn],
  );

  const handleGroupByChange = useCallback(
    (newGroupBy, tableParams = {}) => {
      const previousGroupBy = groupByRef.current;
      setGroupBy(newGroupBy);
      groupByRef.current = newGroupBy;
      const params = buildTrendsParams(tableParams, datePresetRef.current);
      fetchTrendsFn({ ...params, group_by: newGroupBy })
        .then(result => setTrends(result.trends))
        .catch(() => {
          setGroupBy(previousGroupBy);
          groupByRef.current = previousGroupBy;
        });
    },
    [fetchTrendsFn],
  );

  const handleDatePresetChange = useCallback(
    (presetKey, tableParams = {}) => {
      const previousPreset = datePresetRef.current;
      const previousGroupBy = getDefaultGroupBy(previousPreset);
      const newGroupBy = getDefaultGroupBy(presetKey);
      setDatePreset(presetKey);
      setGroupBy(newGroupBy);
      datePresetRef.current = presetKey;
      groupByRef.current = newGroupBy;
      const params = buildTrendsParams(tableParams, presetKey);
      fetchTrendsFn({ ...params, group_by: newGroupBy })
        .then(result => setTrends(result.trends))
        .catch(() => {
          setDatePreset(previousPreset);
          setGroupBy(previousGroupBy);
          datePresetRef.current = previousPreset;
          groupByRef.current = previousGroupBy;
        });
    },
    [fetchTrendsFn],
  );

  return {
    trends,
    groupBy,
    datePreset,
    datePresets: DATE_PRESETS,
    fetchTrends,
    handleGroupByChange,
    handleDatePresetChange,
  };
};
